import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { inr } from "../lib/api";
import { ArrowRight, Inbox } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const StatCard = ({ title, value, sub, icon: Icon, accent = "#0F172A", testId, onClick }) => (
  <Card data-testid={testId} onClick={onClick}
    className={`relative overflow-hidden border-slate-200/80 dark:border-slate-800 hover:shadow-md transition-shadow ${onClick ? "cursor-pointer" : ""}`}>
    <div className="absolute left-0 top-0 h-full w-1" style={{ background: accent }} />
    <CardContent className="p-4 pl-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{title}</p>
          <p className="mt-1 text-2xl font-bold font-display tracking-tight">{value}</p>
          {sub && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{sub}</p>}
        </div>
        {Icon && <div className="rounded-lg p-2" style={{ background: `${accent}18` }}><Icon className="h-4 w-4" style={{ color: accent }} /></div>}
      </div>
    </CardContent>
  </Card>
);

export const PageHeader = ({ title, sub, children, testId }) => (
  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
    <div>
      <h1 data-testid={testId} className="text-2xl font-bold font-display tracking-tight">{title}</h1>
      {sub && <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{sub}</p>}
    </div>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const STATUS_STYLES = {
  Paid: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400",
  Pending: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
  Overdue: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-400",
  Draft: "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300",
  Cancelled: "bg-slate-200 text-slate-500 line-through dark:bg-slate-700/40",
  posted: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400",
  draft: "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300",
  cancelled: "bg-slate-200 text-slate-500 dark:bg-slate-700/40",
};

export const StatusBadge = ({ status }) => {
  const key = Object.keys(STATUS_STYLES).find((k) => (status || "").startsWith(k));
  return (
    <Badge data-testid={`status-${(status || "na").toLowerCase().replace(/\s+/g, "-")}`}
      variant="secondary" className={`${STATUS_STYLES[key] || STATUS_STYLES.Draft} border-0 font-medium`}>
      {status}
    </Badge>
  );
};

export const Money = ({ value, className = "", ...rest }) => (
  <span className={`font-mono tabular-nums ${className}`} {...rest}>{inr(value)}</span>
);

export const EmptyState = ({ title, body, actionLabel, actionTo, testId }) => {
  const nav = useNavigate();
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center py-14 text-center">
      <Inbox className="h-10 w-10 text-slate-300 dark:text-slate-600 mb-3" />
      <p className="font-semibold font-display">{title}</p>
      {body && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm">{body}</p>}
      {actionLabel && actionTo && (
        <Button data-testid={`${testId}-action`} className="mt-4" onClick={() => nav(actionTo)}>
          {actionLabel} <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export const TableSkeleton = ({ rows = 5 }) => (
  <div className="space-y-2 p-4">{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
);

export const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};
